import React, { useState } from "react";
import {
  Navigation,
  Page,
  TaskMultiSelect,
  Storage,
  TaskMultiChoice,
  TaskImageClick,
  Image,
  TaskTabPanel,
  TaskSlider,
  TaskSelect
} from "@bittnerdevelopmentgroup/interactive";
import { Row, Col } from "react-bootstrap";
import panel4a from "./assets/Int_PrinEco5_7A-3_Custom.jpg";
import panel4b from "./assets/Int_PrinEco5_7A-3_CustomRev.png";
import panel1Factory from "./assets/Int_PrinEco5_7A-7_Outsource.jpg";
import panel1Kitchen from "./assets/Int_PrinEco5_7A-8_Outsource.jpg";
import panel1Gardener from "./assets/Int_PrinEco5_7A-9_Outsource.jpg";
import panel1Student from "./assets/Int_PrinEco5_7A-10_Outsource.jpg";
import panel3Tab1 from "./assets/Int_PrinEco5_7A-1_Custom.jpg";
import panel3Tab2 from "./assets/Int_PrinEco5_7A-2_Custom.jpg";
import panel5Tab1 from "./assets/Int_PrinEco5_7A-4_Custom.jpg";
import panel5Tab2 from "./assets/Int_PrinEco5_7A-2_Custom.jpg";
import panel5Tab3 from "./assets/Int_PrinEco5_7A-5_Custom.jpg";
import panel6a from "./assets/Int_PrinEco5_7A-6_Custom.jpg";
import panel6b from "./assets/Int_PrinEco5_7A-6_CustomRev.png";

import "./styles.css"

const InteralizingExternalities = () => {
  const [panel3Tab2Percentage, setPanel3Tab2Percentage] = useState(0);
  const [panel5Tab2Percentage, setPanel5Tab2Percentage] = useState(0);
  return <div>
    <div className="sr-only" aria-hidden="true">
      {[panel4a, panel4b, panel1Factory, panel1Kitchen, panel1Gardener, panel1Student, panel3Tab1, panel5Tab1, panel3Tab2, panel5Tab2, panel5Tab3, panel6a, panel6b].map((v, i) =>
        <img key={"preload-" + i} src={v} alt="" />
      )}
    </div>
    <Navigation interactiveId="chapter-7a" onReset={() => {
      setPanel3Tab2Percentage(0);
      setPanel5Tab2Percentage(0);
    }}>
      <Page>
        <p>
          Let's test your understanding of externalities before you try your hand at correcting them.
        </p>
        <TaskMultiChoice
          state={Storage()}
          prompt={<p>Which of the following is an example of a negative externality?</p>}
          cols={2}
          choices={[
            <>
              <p style={{ minHeight: "2.5rem" }}>
                A factory polluting a nearby river
              </p>
              <Image src={panel1Factory} alt="A factory polluting a nearby river" width="80%" photoCredit={"Artic_photo/Shutterstock"} />
            </>,
            <>
              <p style={{ minHeight: "2.5rem" }}>
                A restaurant installing energy-efficient appliances
              </p>
              <Image src={panel1Kitchen} alt="A restaurant installing energy-efficient appliances" width="80%" photoCredit={"nj.a/Shutterstock"} />
            </>,
            <>
              <p style={{ minHeight: "2.5rem" }}>
                A homeowner planting flowers in their garden
              </p>
              <Image src={panel1Gardener} alt="A homeowner planting flowers in their garden" width="80%" photoCredit={"ADDICTIVE STOCK CREATIVES/Alamy Stock Photo"} />
            </>,
            <>
              <p style={{ minHeight: "2.5rem" }}>
                A student studying hard and getting a poor test grade
              </p>
              <Image src={panel1Student} alt="A student studying hard and getting a poor test grade" width="80%" photoCredit={"fizkes/Shutterstock"} />
            </>,
          ]}
          correct={[0]}
          feedback={[
            "A factory polluting the environment nearby is a classic example of a negative externality. It affects parties (like nearby residents) who are not involved in the production or consumption of the factory's goods.",
            "This is not a negative externality. This action benefits the restaurant and may actually create positive externalities by lowering energy consumption and reducing harmful emissions.",
            "This is not a negative externality. In fact, flowers could even be considered a small positive externality, as they improve the neighborhood's appearance for others.",
            "This is not a negative externality. Getting a poor grade despite studying hard is unfortunate, but it primarily affects the student themselves. Externalities involve effects on third parties not directly involved in an activity.",
          ]}
          randomize={false}
        />
      </Page>
      <Page>
        <TaskMultiSelect
          state={Storage()}
          prompt={<><p>
            As you read in the book, a social optimum is achieved when decision-makers account for not only the costs and benefits to themselves but
            also the costs and benefits to society. In which of the following scenarios has a social optimum been reached? <em>Select all that apply.</em>
          </p></>}
          comboFeedback={[
            { feedback: "Try again – you're missing at least one correct answer. Remember, a decision may benefit the individual, but a social optimum requires weighing the full range of costs and benefits to everyone, not just the private party. Ignoring externalities—positive or negative—means the outcome isn’t socially optimal.", index: [0] },
            { feedback: "Try again – you're missing at least one correct answer. Remember, a decision may benefit the individual, but a social optimum requires weighing the full range of costs and benefits to everyone, not just the private party. Ignoring externalities—positive or negative—means the outcome isn’t socially optimal.", index: [2] },
          ]}
          data={[
            {
              label: (
                <span>
                  A smoker chooses to smoke only in designated areas where secondhand smoke does not affect nonsmokers.
                </span>
              ),
              correct: true,
            },
            {
              label: (
                <span>
                  A professional beekeeper increases the number of hives until the total honey produced maximizes her profits.
                </span>
              ),
              correct: false,
            },
            {
              label: (
                <span>
                  A city uses toll roads to reduce traffic so that the cost of driving includes both private fuel costs and the time delays drivers impose on others.
                </span>
              ),
              correct: true,
            },
          ]}
          correctFeedback={<>
            A social optimum is attained when individuals or firms adjust their behavior to <em>internalize</em> the full impact of their actions on society.
          </>}
          incorrectFeedback={
            <span>
              Try again – you got one or more incorrect. Remember, a decision may benefit the individual, but a social optimum requires weighing the full range of costs and benefits to everyone, not just the private
              party. Ignoring externalities—positive or negative—means the outcome isn't socially optimal.
            </span>
          }
        />
      </Page>
      <Page>
        <TaskTabPanel
          state={Storage()}
          label="An exploration of different economic scenarios with airlines."
          contents={[
            {
              title: "Negative Externality",
              contents: (
                <>
                  <Image width={"90%"} src={panel3Tab1} alt="TK" />
                  <p><b>Let’s visualize what happens in the case of a negative externality.</b></p>
                  <p>
                    In the case of a negative externality, the government may seek to restore the <b>social
                      optimum</b> by requiring the market participants causing the externality to cover the
                    social cost of their actions.
                  </p>
                  <p>
                    Consider the case of airline flights. When flying passengers or goods across the
                    country, airplanes generate a significant amount of noise pollution, which reduces
                    property values for residents living near airports. The noise pollution also disrupts
                    sleep, which can lead to negative health consequences. Let’s look at three potential
                    actions a government might take to reach what economists call a social optimum—the
                    price and quantity combination that would exist if there were no externalities.
                  </p>
                  <p>Explore all tabs to learn more.</p>
                </>
              ),
            },
            {
              title: "Noise Pollution Abatement",
              contents: (
                <>
                  <div style={{width: "90%", height: "520px", overflow: "hidden"}}>
                    <iframe src="https://d1qvht9ud32rg4.cloudfront.net/index.html#prineco5/chapter07a_Panel3_slideronly" title="Panel 3 tab 2" width={"100%"}  style={{ minHeight: "650px", marginTop: "-55px"}} />
                  </div>
                  <p>
                    <b>Implement Flight Path and Time Restrictions </b><em>Shift the slider to see how implementing flight restrictions shifts the supply curve from the market equilibrium toward the social optimum.</em>
                  </p>
                  <p>
                    The government may require airports and airlines to follow mandated flight paths,
                    with curfews limiting flights during sensitive hours to reduce engine noise.
                  </p>
                  <p>
                    The new regulations directly lower the negative impact on the environment and public
                    health. However, compliance can be very costly to the airlines, as they must limit flight
                    routes and available flight times, which leads to fewer flight options. The effect of the
                    higher costs is to shift the supply curve to the left.
                  </p>
                </>
              ),
            },
            {
              title: "Production Tax",
              contents: (
                <>
                  <Image width={"90%"} src={panel3Tab2} alt="to come" />
                  <p>
                    <b>Levy a Production Tax </b><em>How does this solution address the externality? How does it impact the supply and demand graph?</em>
                  </p>
                  <div className="inline-task-select-combo">
                    <div style={{ marginBottom: "1rem" }}>
                      A tax on production would make airlines pay for using noisy aircraft, especially at night. This would create an economic incentive for
                      airlines to either cut back the number of flights or produce quieter airplanes.
                    </div>
                    <span aria-hidden="true">
                      Whichever the airlines choose to do, a production tax will cause the costs they face to
                    </span>
                    <span className="first-task-select">
                      <TaskSelect
                        state={Storage()}
                        options={["increase", "decrease"]}
                        feedback={[undefined, "Please try again."]}
                        label={<span>Whichever the airlines choose to do, a production tax will cause the costs they face to</span>}
                        description={<span>.</span>}
                        correct={0}
                        inline
                        srOnly
                      />
                    </span>
                    <span aria-hidden="true">. </span>
                    <span aria-hidden="true">Facing higher costs, the supply curve shifts to the</span>
                    <TaskSelect
                      state={Storage()}
                      options={["left", "right"]}
                      feedback={[undefined, "Please try again."]}
                      label={<span>Facing higher costs, the supply curve shifts to the</span>}
                      description={<span>.</span>}
                      correct={0}
                      inline
                      srOnly
                    />
                    <span aria-hidden="true">.</span>
                  </div>
                </>
              ),
            },
            {
              title: "Environmental Liability",
              contents: (
                <>
                  <Image width={"90%"} src={panel3Tab2} alt="TK" />
                  <p>
                    <b>Enforce Environmental Liability </b><em>How does this solution address the externality? How does it impact the supply and demand graph?</em>
                  </p>
                  <div className="inline-task-select-combo">
                    <div style={{ marginBottom: "1rem" }}>
                      The government could hold airports and airlines financially liable for the damage their noise pollution caused to nearby
                      residents' well-being. The threat of these financial consequences could encourage minimizing noise pollution to avoid future
                      liabilities.
                    </div>
                    <span aria-hidden="true">
                      Thus, airlines would see
                    </span>
                    <TaskSelect
                      state={Storage()}
                      options={["higher", "lower"]}
                      feedback={[undefined, "Please try again."]}
                      label={<span>
                        Thus, airlines would see
                      </span>}
                      description={<span>
                        costs regardless of whether they kept their present technology and paid for the damage it caused or invested in better production techniques to
                        reduce noise pollution from their aircraft. Those costs would shift the supply curve to the left.
                      </span>}
                      correct={0}
                      inline
                      srOnly
                    />
                    <span aria-hidden="true">
                      costs regardless of whether they kept their present technology and paid for the damage it caused or invested in better production techniques
                      to reduce noise pollution from their aircraft. </span>
                    <span aria-hidden="true">Those costs would shift the supply curve to the </span>
                    <TaskSelect
                      state={Storage()}
                      options={["left", "right"]}
                      feedback={[undefined, "Please try again."]}
                      label={<span>Those costs would shift the supply curve to the </span>}
                      description={<span>.</span>}
                      correct={0}
                      inline
                      srOnly
                    />
                    <span aria-hidden="true">.</span>
                  </div>
                </>
              ),
            },
            {
              title: "Conclusion",
              contents: (
                <>
                  <Image width={"90%"} src={panel3Tab2} alt="TK" />
                  <p>
                    As you’ve just seen, all three corrective measures require the airlines to spend money
                    to correct the negative externality, thereby increasing their overall cost of production.
                    This reduces the amount of pollution-causing activity, shifting the supply curve to the
                    left. S<sub>social</sub>, the new supply curve, reflects a combination of the internal and external
                    costs of producing flights. The result is a social optimum at a lower quantity, Q<sub>S</sub>, than
                    the market equilibrium quantity, Q<sub>M</sub>.
                  </p>
                  <p>
                    We can reduce negative production externalities by requiring producers to internalize
                    the externality. However, there is a trade-off. Because the supply curve shifts to the
                    left, the quantity produced falls, and the price rises.
                  </p>
                </>
              ),
            },
          ]}
        />
      </Page>
      <Page>
        <div className="externalities-panel-4a">
          <p>Select the area containing the social optimum point.</p>
          <TaskImageClick
            state={Storage()}
            textAdjust
            hotspots={[
              {
                correct: true,
                label: "Red Circle",
                feedback: <span>This is the social optimum, because this is the point where S<sub>social</sub> intersects D<sub>internal</sub>.</span>,
                shape: "Circle",
                style: {
                  top: "30.2%",
                  left: "34.3%",
                  width: "7.5%",
                  height: "10%",
                },
              },
              {
                correct: false,
                label: "Orange Circle",
                feedback: <span>
                  This is the market equilibrium, because this is the point where S<sub>internal</sub> intersects D<sub>internal</sub>. The negative
                  externality has no impact on this equilibrium point. The social optimum is the ideal outcome for the market when the cost from the
                  externality is taken into account.
                </span>,
                shape: "Circle",
                style: {
                  top: "37.7%",
                  left: "41%",
                  width: "7.5%",
                  height: "10%",
                },
              },
            ]}
          >
            <Image
              alt="TK"
              src={panel4a}
            />
          </TaskImageClick>
        </div>
      </Page>
      <Page>
        <div className="externalities-panel-4b">
          <p>Select the area representing the deadweight loss that results from this externality.</p>
          <TaskImageClick
            state={Storage()}
            textAdjust
            hotspots={[
              {
                correct: true,
                label: "Triangle (backwards play button)",
                shape: "Circle",
                feedback: <span>
                  This is the area of deadweight loss caused by overproduction of a good that generates a negative externality. As the market moves
                  from the market equilibrium to the social optimum (internalizing the externality), deadweight loss is eliminated.
                </span>,
                style: {
                  top: "24.5%",
                  left: "47.9%",
                  width: "6.75%",
                  height: "9.5%",
                },
              },
              {
                correct: false,
                label: "Triangle (play button)",
                shape: "Circle",                
                feedback: <span>
                  This is not the area of deadweight loss. Remember, for negative production externalities, the deadweight loss is triangle
                  shaped and represents the loss in consumer and producer surplus due to overproduction. The area of this triangle is bound
                  right and left by the market equilibrium quantity and the socially optimal quantity, and it is bound above
                  and below by S<sub>social</sub> and D<sub>internal</sub>.
                </span>,
                style: {
                  top: "43.7%",
                  left: "28.6%",
                  width: "6.75%",
                  height: "9.5%",
                },
              },
              {
                correct: false,
                label: "Trapezoid",
                shape: "Circle",                
                feedback: <span>
                  This is not the area of deadweight loss. Remember, for negative production externalities, the deadweight loss is triangle
                  shaped and represents the loss in consumer and producer surplus due to overproduction. The area of this triangle is
                  bound right and left by the market equilibrium quantity and the socially optimal quantity, and it is bound above
                  and below by S<sub>social</sub> and D<sub>internal</sub>.
                </span>,
                style: {
                  top: "60.9%",
                  left: "47.3%",
                  width: "6.75%",
                  height: "9.5%",
                },
              },
            ]}
          >
            <Image
              alt="TK"
              src={panel4b}
            />
          </TaskImageClick>
        </div>
      </Page>
      <Page>
        <TaskTabPanel
          state={Storage()}
          label="Price of Fire Extinguishers"
          contents={[
            {
              title: "Positive Externality",
              contents: (
                <>
                  <Image width={"90%"} src={panel5Tab1} alt="" />
                  <p>
                    <b>Now let's visualize what happens in the case of a positive externality.</b>
                  </p>
                  <p>
                    Consider a renter who buys a fire extinguisher for their apartment. The improved protection from the consequences of a small
                    kitchen fire is an internal benefit. But neighboring tenants are also better off, because there is less risk of a large fire
                    spreading throughout the building. Thus, fire extinguishers provide a positive externality.
                  </p>
                  <p>
                    Suppose that few apartment renters in an urban area have invested in fire extinguishers. If the city government is concerned
                    about this, what can it do? Review all the tabs to learn about three potential solutions.
                  </p>
                </>
              ),
            },
            {
              title: "Renter Requirement",
              contents: (
                <>
                  <div style={{width: "90%", height: "520px", overflow: "hidden"}}>
                    <iframe src="https://tabbed-figure-499308.gitlab.io/index.html#prineco5/chapter07a_Panel5_slideronly" title="Panel 5 tab 2" width={"100%"} style={{ minHeight: "630px", marginTop: "-55px" }} />
                  </div>
                  <p>
                    <b>Require renters to own a fire extinguisher.</b> <em>Shift the slider to see how requiring renters
                      to own fire extinguishers impacts the demand curve.</em>
                  </p>
                  <p>
                    If all renters are required to have fire extinguishers on hand, the demand for fire extinguishers increases. An
                    increase in demand is illustrated by a rightward shift of the demand curve.
                  </p>
                </>
              ),
            },
            {
              title: "Landlord requirement",
              contents: (
                <>
                  <Image width={"90%"} src={panel5Tab3} alt="" />
                  <p>
                    <b>Require landlords to provide a fire extinguisher.</b> <em>How does this solution address the
                      externality? How does it impact the supply and demand graph?</em>
                  </p>
                  <p>
                    Requiring landlords to provide fire extinguishers for tenants is another way of
                    reducing the risk of large apartment fires. 
                  </p>
                  <span aria-hidden="true">With such a requirement in place, the demand for fire extinguishers </span>
                  <TaskSelect
                    state={Storage()}
                    options={["increases", "decreases"]}
                    feedback={[undefined, "Please try again."]}
                    label={<span>With such a requirement in place, the demand for fire extinguishers </span>}
                    description={<span>. </span>}
                    correct={0}
                    inline
                    srOnly
                  />
                  <span aria-hidden="true">. </span>
                  <span aria-hidden="true">This is illustrated by a </span>
                  <TaskSelect
                    state={Storage()}
                    options={["rightward", "leftward"]}
                    feedback={[undefined, "Please try again."]}
                    label={<span>This is illustrated by a </span>}
                    description={<span> shift of the demand curve.</span>}
                    correct={0}
                    inline
                    srOnly
                  />
                  <span aria-hidden="true">shift of the demand curve.</span>
                </>
              ),
            },
            {
              title: "Subsidy",
              contents: (
                <>
                  <Image width={"90%"} src={panel5Tab3} alt="" />
                  <p>
                    <b>Subsidize the purchase of fire extinguishers. </b> <em>How does this solution address the 
                      externality? How does it impact the supply and demand graph?</em>
                  </p>
                  <p>
                    Subsidizing the purchase of fire extinguishers lowers the cost to the renter or landlord. This 
                    will encourage more renters or landlords to buy extinguishers, which will shift the demand 
                    curve to the right. (While there is currently no federal subsidy for fire extinguishers, some local 
                    fire departments offer discounted rates on extinguishers as a way to encourage fire safety.)
                  </p>
                </>
              ),
            },
            {
              title: "Conclusion",
              contents: (
                <>
                <Image width={"90%"} src={panel5Tab3} alt="" />
                  <p>
                    As you've just seen, adjusting demand to factor in the benefit fire extinguishers have on others increases the number
                    of fire extinguishers purchased. This result is evident in the shift of the demand curve to D<sub>social</sub>. The new demand
                    curve reflects a combination of the internal and external benefits of owning a fire extinguisher. The result is a
                    social optimum at a higher quantity, Q<sub>S</sub>, than the market equilibrium quantity, Q<sub>M</sub>.
                  </p>
                  <p>
                    So, we can reduce positive consumption externalities by encouraging consumers to internalize the externality. However, once
                    again there is a trade-off. Because the demand curve shifts to the right, the price of the good rises.
                  </p>
                </>
              )
            }
          ]}
        />
      </Page>
      <Page>
        <div className="externalities-panel-6a">
          <p>Select the area containing the social optimum point.</p>
          <TaskImageClick
            state={Storage()}
            textAdjust
            hotspots={[
              {
                correct: true,
                label: "Red Circle",
                feedback: <span>This is the social optimum, because this is the point where D<sub>social</sub> intersects S<sub>internal</sub>.</span>,
                shape: "Circle",
                style: {
                  top: "33.4%",
                  left: "56.55%",
                  width: "7.5%",
                  height: "10%",
                },
              },
              {
                correct: false,
                label: "Orange Circle",
                feedback: <span>
                  This is the market equilibrium, because this is the point where D<sub>internal</sub> intersects S<sub>internal</sub>. The positive
                  externality has no impact on this equilibrium point. The social optimum is the ideal outcome for the market when the benefit from
                  the positive externality is taken into account.
                </span>,
                shape: "Circle",
                style: {
                  top: "40.2%",
                  left: "49.7%",
                  width: "7.5%",
                  height: "10%",
                },
              },
            ]}
          >
            <Image
              alt="TK"
              src={panel6a}
            />
          </TaskImageClick>
        </div>
      </Page>
      <Page>
        <div className="externalities-panel-6b">
          <p>Select the area representing the deadweight loss that results from this externality.</p>
          <TaskImageClick
            state={Storage()}
            textAdjust
            hotspots={[
              {
                correct: true,
                label: "Triangle (play button)",
                shape: "Circle",                
                feedback: <span>
                  This is the area of deadweight loss caused by underconsumption of a good that generates a positive externality. As the
                  market moves from the market equilibrium to the social optimum (internalizing the externality), deadweight loss is eliminated.
                </span>,
                style: {
                  top: "25.5%",
                  left: "44.8%",
                  width: "6.17%",
                  height: "8.5%",
                },
              },
              {
                correct: false,
                label: "Triangle (backwards play button)",
                shape: "Circle", 
                feedback: <span>
                  This is not the area of deadweight loss. Remember, for positive consumption externalities, the deadweight loss is
                  triangle shaped and represents the amount of consumer and producer surplus the market missed out on due to underconsumption. The
                  area of this triangle is bound left and right by the market equilibrium quantity and the socially optimal quantity, and it is
                  bound above and below by D<sub>social</sub> and S<sub>internal</sub>.
                </span>,
                style: {
                  top: "45%",
                  left: "62.5%",
                  width: "6.17%",
                  height: "8.5%",
                },
              },
              {
                correct: false,
                label: "Trapezoid",
                shape: "Circle", 
                feedback: <span>
                  This is not the area of deadweight loss. Remember, for positive consumption externalities, the deadweight loss is triangle shaped
                  and represents the amount of consumer and producer surplus the market missed out on due to underconsumption. The area of this
                  triangle is bound left and right by the market equilibrium quantity and the socially optimal quantity, and it is bound above
                  and below by D<sub>social</sub> and S<sub>internal</sub>.
                </span>,
                style: {
                  top: "59.8%",
                  left: "45.3%",
                  width: "6.17%",
                  height: "8.5%",
                },
              },
            ]}
          >
            <Image
              alt="TK"
              src={panel6b}
            />
          </TaskImageClick>
        </div>
      </Page>
    </Navigation>
  </div>
}

export default InteralizingExternalities